import { RefObject, useEffect, useState } from 'react';

import {
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  GridApi,
} from '@mui/x-data-grid-premium';

const DRAGGED_HEADER_CLASS = 'MuiDataGrid-columnHeader--dragging';
const CELL_HIGHLIGHT_CLASS = 'GeneratikTable-draggedColumnCell';
const BODY_DRAG_CLASS = 'GeneratikTable-columnDragging';
const BODY_STYLE_ID = 'generatik-column-drag-style';
const SCROLL_ZONE_PX = 80;
const MAX_SCROLL_PX_PER_FRAME = 40;
// A press only becomes a drag once the pointer travels this far. Decorating the
// header on pointerdown would disable its pointer-events during a plain click,
// so the browser would retarget the click at the header row and the grid's
// columnHeaderClick (header-click sorting) would never fire.
const DRAG_START_THRESHOLD_PX = 4;
const SYSTEM_FIELDS = new Set([
  GRID_CHECKBOX_SELECTION_FIELD,
  GRID_ROW_GROUPING_SINGLE_GROUPING_FIELD,
  GRID_TREE_DATA_GROUPING_FIELD,
  'actions',
]);
const INTERACTIVE_SELECTOR =
  'button, [role="button"], a, input, .MuiDataGrid-menuIcon, .MuiDataGrid-iconButtonContainer, .MuiDataGrid-sortIcon, .MuiDataGrid-columnSeparator';

const PINNED_LEFT_CLASS = 'MuiDataGrid-columnHeader--pinnedLeft';
const PINNED_RIGHT_CLASS = 'MuiDataGrid-columnHeader--pinnedRight';

const isPinnedHeader = (el: HTMLElement) =>
  el.classList.contains(PINNED_LEFT_CLASS) || el.classList.contains(PINNED_RIGHT_CLASS);

const getUnionRect = (els: HTMLElement[]) => {
  if (els.length === 0) return null;
  const rects = els.map((el) => el.getBoundingClientRect());
  return {
    left: Math.min(...rects.map((r) => r.left)),
    right: Math.max(...rects.map((r) => r.right)),
    top: Math.min(...rects.map((r) => r.top)),
    bottom: Math.max(...rects.map((r) => r.bottom)),
  };
};

const getPinnedAreaRects = (container: HTMLElement) => {
  const leftEls = Array.from(container.querySelectorAll<HTMLElement>(`.${PINNED_LEFT_CLASS}`));
  const rightEls = Array.from(container.querySelectorAll<HTMLElement>(`.${PINNED_RIGHT_CLASS}`));
  return { left: getUnionRect(leftEls), right: getUnionRect(rightEls) };
};

const resolveDropTarget = (container: HTMLElement, draggedField: string, x: number, y: number) => {
  const el = document.elementFromPoint(x, y);
  let header = el?.closest<HTMLElement>('.MuiDataGrid-columnHeader') ?? null;

  if (!header) {
    const cell = el?.closest<HTMLElement>('.MuiDataGrid-cell');
    const cellField = cell?.getAttribute('data-field');
    if (cellField) {
      header = container.querySelector<HTMLElement>(
        `.MuiDataGrid-columnHeader[data-field="${cellField}"]`,
      );
    }
  }

  if (!header || !container.contains(header)) return null;
  if (isPinnedHeader(header)) return null;
  const field = header.getAttribute('data-field');
  if (!field || field === draggedField || SYSTEM_FIELDS.has(field)) return null;
  const rect = header.getBoundingClientRect();
  const side: 'before' | 'after' = x < rect.left + rect.width / 2 ? 'before' : 'after';
  return { header, field, rect, side };
};

const useDraggedColumnHighlight = (
  containerRef: RefObject<HTMLElement | null>,
  apiRef: RefObject<GridApi>,
): boolean => {
  // True only while a column header is being dragged. Consumers render all
  // columns during the drag (the highlight/drop logic needs them in the DOM) and
  // let columns virtualize normally otherwise, keeping rows cheap to scroll.
  const [isDraggingColumn, setIsDraggingColumn] = useState(false);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.getElementById(BODY_STYLE_ID)) {
      const styleEl = document.createElement('style');
      styleEl.id = BODY_STYLE_ID;
      styleEl.textContent = `
        body.${BODY_DRAG_CLASS} .MuiTooltip-popper,
        body.${BODY_DRAG_CLASS} .MuiPopper-root {
          display: none !important;
        }
      `;
      document.head.appendChild(styleEl);
    }

    let draggedField: string | null = null;
    let draggedHeader: HTMLElement | null = null;
    let dragStarted = false;
    let startX = 0;
    let startY = 0;
    let initialDraggedIndex = -1;
    let lastReorderKey: string | null = null;
    let clientX = -1;
    let clientY = -1;
    let previousUserSelect = '';
    let rafId = 0;

    const reapplyCellHighlight = () => {
      if (!draggedField) return;
      container
        .querySelectorAll(`.MuiDataGrid-cell[data-field="${draggedField}"]`)
        .forEach((el) => el.classList.add(CELL_HIGHLIGHT_CLASS));
    };

    const resetHeaderStyles = (el: HTMLElement) => {
      el.classList.remove(DRAGGED_HEADER_CLASS);
      el.style.pointerEvents = '';
    };

    const clearHighlight = () => {
      container
        .querySelectorAll(`.${CELL_HIGHLIGHT_CLASS}`)
        .forEach((el) => el.classList.remove(CELL_HIGHLIGHT_CLASS));
      // Sweep every header instead of querying by the drag marker: React
      // rewrites a header's className whenever a position-derived class such
      // as MuiDataGrid-columnHeader--last toggles mid-drag, which strips the
      // manual marker while the imperative pointer-events style survives.
      container
        .querySelectorAll<HTMLElement>('.MuiDataGrid-columnHeader')
        .forEach(resetHeaderStyles);
    };

    const applyHighlight = (field: string, header: HTMLElement) => {
      header.classList.add(DRAGGED_HEADER_CLASS);
      header.style.pointerEvents = 'none';
      reapplyCellHighlight();
    };

    const maybeReorder = () => {
      if (!draggedField || !apiRef.current?.setColumnIndex) return;
      const target = resolveDropTarget(container, draggedField, clientX, clientY);
      if (!target) return;
      const key = `${target.field}-${target.side}`;
      if (key === lastReorderKey) return;
      lastReorderKey = key;

      const allCols = apiRef.current.getAllColumns();
      const targetIdx = allCols.findIndex((c) => c.field === target.field);
      if (targetIdx < 0) return;
      const insertIdx = target.side === 'after' ? targetIdx + 1 : targetIdx;
      apiRef.current.setColumnIndex(draggedField, insertIdx);

      const currentHeader = container.querySelector<HTMLElement>(
        `.MuiDataGrid-columnHeader[data-field="${draggedField}"]`,
      );
      container.querySelectorAll<HTMLElement>(`.${DRAGGED_HEADER_CLASS}`).forEach((el) => {
        if (el !== currentHeader) resetHeaderStyles(el);
      });
      if (currentHeader && currentHeader !== draggedHeader) {
        draggedHeader = currentHeader;
        currentHeader.classList.add(DRAGGED_HEADER_CLASS);
        currentHeader.style.pointerEvents = 'none';
      }
      reapplyCellHighlight();
    };

    const beginDrag = () => {
      if (dragStarted || !draggedField || !draggedHeader) return;
      dragStarted = true;
      previousUserSelect = document.body.style.userSelect;
      document.body.style.userSelect = 'none';
      document.body.classList.add(BODY_DRAG_CLASS);
      setIsDraggingColumn(true);
      applyHighlight(draggedField, draggedHeader);
    };

    const onPointerDown = (e: PointerEvent) => {
      if (e.button !== 0) return;
      const target = e.target as HTMLElement | null;
      if (!target) return;
      if (target.closest(INTERACTIVE_SELECTOR)) return;

      const header = target.closest<HTMLElement>('.MuiDataGrid-columnHeader');
      if (!header) return;

      const field = header.getAttribute('data-field');
      if (!field || SYSTEM_FIELDS.has(field)) return;

      e.preventDefault();

      draggedField = field;
      draggedHeader = header;
      dragStarted = false;
      startX = e.clientX;
      startY = e.clientY;
      clientX = e.clientX;
      clientY = e.clientY;
      lastReorderKey = null;
      initialDraggedIndex =
        apiRef.current?.getAllColumns().findIndex((c) => c.field === field) ?? -1;

      document.addEventListener('pointermove', onDragMove);
      document.addEventListener('pointerup', onDragEnd);
      document.addEventListener('pointercancel', onDragEnd);
    };

    const onDragMove = (e: PointerEvent) => {
      e.preventDefault();
      clientX = e.clientX;
      clientY = e.clientY;
      if (!dragStarted) {
        if (
          Math.abs(clientX - startX) < DRAG_START_THRESHOLD_PX &&
          Math.abs(clientY - startY) < DRAG_START_THRESHOLD_PX
        ) {
          return;
        }
        beginDrag();
      }
      maybeReorder();
    };

    const onDragEnd = () => {
      document.removeEventListener('pointermove', onDragMove);
      document.removeEventListener('pointerup', onDragEnd);
      document.removeEventListener('pointercancel', onDragEnd);

      const didDrag = dragStarted;
      if (didDrag) {
        document.body.style.userSelect = previousUserSelect;
        document.body.classList.remove(BODY_DRAG_CLASS);
        setIsDraggingColumn(false);

        container.querySelectorAll('.MuiDataGrid-columnHeader').forEach((h) => {
          h.dispatchEvent(new MouseEvent('mouseleave', { bubbles: false }));
          h.dispatchEvent(new MouseEvent('mouseout', { bubbles: true }));
          h.dispatchEvent(new PointerEvent('pointerleave', { bubbles: false }));
          h.dispatchEvent(new PointerEvent('pointerout', { bubbles: true }));
        });
      }

      const finalField = draggedField;
      const startIndex = initialDraggedIndex;

      clearHighlight();
      draggedField = null;
      draggedHeader = null;
      dragStarted = false;
      lastReorderKey = null;
      initialDraggedIndex = -1;

      if (didDrag && finalField && startIndex !== -1 && apiRef.current) {
        const cols = apiRef.current.getAllColumns();
        const newIndex = cols.findIndex((c) => c.field === finalField);
        if (newIndex !== -1 && newIndex !== startIndex) {
          const column = cols[newIndex];
          try {
            (
              apiRef.current as unknown as {
                publishEvent: (name: string, params: unknown) => void;
              }
            ).publishEvent('columnOrderChange', {
              column,
              targetIndex: newIndex,
              oldIndex: startIndex,
            });
          } catch {
            // ignore if event name differs in MUI X version
          }
        }
      }
    };

    const reassertHighlight = () => {
      if (!draggedField) return;
      const currentHeader = container.querySelector<HTMLElement>(
        `.MuiDataGrid-columnHeader[data-field="${draggedField}"]`,
      );
      if (!currentHeader) return;
      if (!currentHeader.classList.contains(DRAGGED_HEADER_CLASS)) {
        currentHeader.classList.add(DRAGGED_HEADER_CLASS);
        currentHeader.style.pointerEvents = 'none';
        draggedHeader = currentHeader;
      }
      reapplyCellHighlight();
    };

    const tick = () => {
      if (dragStarted && draggedField && clientX >= 0) {
        // React re-renders can strip the manual decorations mid-drag (e.g. the
        // --last class toggling rewrites the header className) and auto-scroll
        // virtualizes new cells in — re-assert both every frame while dragging.
        reassertHighlight();
        const scroller = container.querySelector<HTMLElement>('.MuiDataGrid-virtualScroller');
        if (scroller) {
          const scrollerRect = scroller.getBoundingClientRect();
          const { left: leftPinnedRect, right: rightPinnedRect } = getPinnedAreaRects(container);

          const scrollableLeft = leftPinnedRect ? leftPinnedRect.right : scrollerRect.left;
          const scrollableRight = rightPinnedRect ? rightPinnedRect.left : scrollerRect.right;
          const maxScroll = scroller.scrollWidth - scroller.clientWidth;

          const rightDepth = scrollableRight - clientX;
          const leftDepth = clientX - scrollableLeft;

          if (rightDepth <= SCROLL_ZONE_PX && scroller.scrollLeft < maxScroll) {
            const intensity = Math.min(
              1,
              (SCROLL_ZONE_PX - Math.max(0, rightDepth)) / SCROLL_ZONE_PX,
            );
            scroller.scrollLeft = Math.min(
              maxScroll,
              scroller.scrollLeft + intensity * MAX_SCROLL_PX_PER_FRAME,
            );
          } else if (leftDepth <= SCROLL_ZONE_PX && scroller.scrollLeft > 0) {
            const intensity = Math.min(
              1,
              (SCROLL_ZONE_PX - Math.max(0, leftDepth)) / SCROLL_ZONE_PX,
            );
            scroller.scrollLeft = Math.max(
              0,
              scroller.scrollLeft - intensity * MAX_SCROLL_PX_PER_FRAME,
            );
          }

          maybeReorder();
        }
      }
      rafId = requestAnimationFrame(tick);
    };

    container.addEventListener('pointerdown', onPointerDown);
    rafId = requestAnimationFrame(tick);

    return () => {
      container.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('pointermove', onDragMove);
      document.removeEventListener('pointerup', onDragEnd);
      document.removeEventListener('pointercancel', onDragEnd);
      document.body.classList.remove(BODY_DRAG_CLASS);
      cancelAnimationFrame(rafId);
      clearHighlight();
    };
  }, [containerRef, apiRef]);

  return isDraggingColumn;
};

export default useDraggedColumnHighlight;
